export function readUrlString(sp: URLSearchParams, key: string): string | undefined {
  const v = sp.get(key);
  return v === null || v === '' ? undefined : v;
}

export function readUrlNumber(sp: URLSearchParams, key: string): number | undefined {
  const v = sp.get(key);
  if (v === null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Counts cannot be zero, negative or fractional: page, items_per_page. `max` clamps down rather
 * than rejecting, so an out-of-range page size lands on a value the size changer actually offers
 * instead of dropping to the default.
 */
export function readUrlCount(sp: URLSearchParams, key: string, max?: number): number | undefined {
  const n = readUrlNumber(sp, key);
  if (n === undefined) return undefined;

  const floored = Math.floor(n);
  if (floored < 1) return undefined;

  return max === undefined ? floored : Math.min(floored, max);
}

export function readUrlBool(sp: URLSearchParams, key: string): boolean | undefined {
  const v = sp.get(key);
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
}

type SerializableValue = string | number | boolean | null | undefined;

type ParamsFromStateOptions = {
  skip?: string[];
  defaults?: Record<string, SerializableValue>;
};

type TimeRangeInit<T extends string = string> = {
  time_range: T | undefined;
  start_time: string | undefined;
  end_time: string | undefined;
};

export function resolveInitialTimeRange<T extends string>(
  url: URLSearchParams,
  storeTimeRange: string | undefined,
  storeStart: string | undefined,
  storeEnd: string | undefined,
  defaultTimeRange: T
): TimeRangeInit<T> {
  const urlStart = readUrlString(url, 'start_time');
  const urlEnd = readUrlString(url, 'end_time');
  const urlTimeRange = readUrlString(url, 'time_range');

  if (urlStart && urlEnd) {
    return { time_range: undefined, start_time: urlStart, end_time: urlEnd };
  }
  if (urlTimeRange) {
    return { time_range: urlTimeRange as T, start_time: undefined, end_time: undefined };
  }
  if (storeStart && storeEnd) {
    return { time_range: undefined, start_time: storeStart, end_time: storeEnd };
  }
  return {
    time_range: (storeTimeRange as T | undefined) ?? defaultTimeRange,
    start_time: undefined,
    end_time: undefined,
  };
}

export function paramsFromState(
  state: Record<string, SerializableValue>,
  { skip = [], defaults = {} }: ParamsFromStateOptions = {}
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(state)) {
    if (skip.includes(key)) continue;
    if (value === undefined || value === null || value === '') continue;
    if (Object.prototype.hasOwnProperty.call(defaults, key) && defaults[key] === value) continue;
    params.set(key, String(value));
  }
  params.sort();
  return params;
}
